import { SVGAssets } from '@business/assets/vectors';
import React, { useState, useCallback, useRef, useEffect, FC } from 'react';
import { useDropzone } from 'react-dropzone';
import { uploadToS3, validateImageFile } from '@utils/helpers/s3Upload';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getToken } from '@ports/common/sessionStateSlice';
import { useAddUserDetailsMutation } from '@ports/internal-services/userDetails/userDetailsSlice';
import { setAlertDetails } from '@ports/common/AlertStateSlice';
import { routePaths } from '@utils/constants/routePaths';
import { useNavigate } from 'react-router-dom';
import {
  useGetUserAssetsQuery,
  useLazyGetAssetsByIdQuery,
  type UserAssets
} from '@ports/internal-services/asset/assetSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { useUpdatePeopleMutation } from '@ports/internal-services/people/peopleSlice';
import { extractFaceFromImage } from '@utils/helpers/lib/photoHelpers/extractFaceFromImage';
import { Button } from '../Button';
import { UploadErrorBlock } from '../UploadFilesModal';
import { ImageCropperModal } from './ImageCropperModal';

interface UploadPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onUploadSuccess?: () => void; // Add this prop
  isPeopleProfilePic: boolean;
  peopleId?: string;
  peopleType?: string; // 'family' | 'friend' | 'owner' — passed through to updatePeople body
  skipUpload?: boolean; // If true, returns file without uploading
  onFileSelect?: (file: File) => void; // Callback to return selected file
  skipNavigateOnSuccess?: boolean; // If true, don't redirect to My Profile after a successful user profile image update
  hideGallerySection?: boolean; // If true, hide the "pick from existing photos" gallery section
}

// Extend File to include preview property
type FileWithPreview = File & { preview: string };

export const UploadPhotoModal: FC<UploadPhotoModalProps> = ({
  isOpen,
  onClose,
  userId,
  onUploadSuccess,
  isPeopleProfilePic,
  peopleId,
  peopleType,
  skipUpload = false,
  onFileSelect,
  skipNavigateOnSuccess = false,
  hideGallerySection = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector(getToken);
  const userDetails = useSelector(getUserData);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [errorFiles, setErrorFiles] = useState<File[]>([]); // To track files that failed validation
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');

  const [selectedPhoto, setSelectedPhoto] = useState<
    UserAssets['rows'][0] | null
  >(null);
  const [isViewMore, setIsViewMore] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const [UpdateUserDetails] = useAddUserDetailsMutation();
  const [UpdatePeople] = useUpdatePeopleMutation();

  const [getAssetsById] = useLazyGetAssetsByIdQuery();

  const [filterOptions, setFilterOptions] = useState<any>({
    limit: 4,
    offset: 0,
    mediaType: ['image']
  });

  const { data: assetData, refetch: assetRefetch } = useGetUserAssetsQuery(
    filterOptions,
    {
      refetchOnMountOrArgChange: true
    }
  );

  useEffect(() => {
    if (isViewMore) {
      setFilterOptions({ mediaType: ['image'] });
    } else {
      setFilterOptions({ limit: 4, offset: 0, mediaType: ['image'] });
    }
  }, [isViewMore]);

  useEffect(() => {
    assetRefetch();
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setError('');
    const file = acceptedFiles[0];

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      setErrorFiles(prev => [...prev, file]);
      return;
    }

    // Create initial image URL
    const imageUrl = URL.createObjectURL(file);

    // Open cropper with square image
    setSelectedImageUrl(imageUrl);
    setShowCropper(true);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false
  });

  const handleCropComplete = (croppedFile: File) => {
    // Add cropped file to preview
    const fileWithPreview = Object.assign(croppedFile, {
      preview: URL.createObjectURL(croppedFile)
    }) as FileWithPreview;
    setFiles([fileWithPreview]);
    setShowCropper(false);
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl);
    }
    setSelectedImageUrl('');
    setSelectedPhoto(null);

    // If skipUpload mode, immediately pass the cropped file back
    if (skipUpload && onFileSelect) {
      onFileSelect(croppedFile);
      setFiles([]);
      setError('');
      setErrorFiles([]);
      onClose();
    }
  };

  const handleCropperClose = () => {
    setShowCropper(false);
    if (selectedImageUrl) {
      URL.revokeObjectURL(selectedImageUrl);
      setSelectedImageUrl('');
    }
  };

  const handleUploadComplete = async () => {
    // After successful upload
    if (onUploadSuccess) {
      await onUploadSuccess();
    }
    onClose();
    setError('');
    setErrorFiles([]);
  };

  const handleUpload = async (imageFile?: File) => {
    let fileToUpload: File | undefined = imageFile;
    if (!fileToUpload) {
      if (files.length === 0) {
        setError('Please select an image first');
        return;
      }
      fileToUpload = files[0];

      if (!userId && !skipUpload) {
        setError('User ID not found');
        return;
      }
    }

    // If skipUpload mode, just return the file
    if (skipUpload && onFileSelect) {
      onFileSelect(fileToUpload);
      setFiles([]);
      setError('');
      setErrorFiles([]);
      onClose();
      return;
    }

    setUploading(true);
    setError('');

    try {
      const idToken = token || '';

      if (!idToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const imageUrl = await uploadToS3(
        fileToUpload,
        idToken,
        userId,
        'profile',
        '',
        {
          userId: userDetails?.userId
        }
      );

      if (isPeopleProfilePic) {
        const dataObj = {
          id: peopleId || '',
          updatePeople: {
            profileImage: imageUrl,
            ...(peopleType && { type: peopleType })
          }
        };

        const res: any = await UpdatePeople(dataObj);
        if (res?.data) {
          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: t('proPicSuccess')
              },
              scope: 'people'
            })
          );
          await handleUploadComplete();
          onClose();
          setError('');
          setErrorFiles([]);
        } else {
          throw new Error(res?.error || 'Failed to update people profile');
        }
      } else {
        const dataObj = {
          createUserDetails: {
            profileImage: imageUrl
          }
        };

        const res: any = await UpdateUserDetails(dataObj);

        if (res?.data) {
          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: t('applySuccess')
              },
              scope: 'profile'
            })
          );
          if (!skipNavigateOnSuccess) {
            navigate(routePaths.MyProfile);
          }
        } else {
          throw new Error(res?.error || 'Failed to update user profile');
        }
      }
      handleUploadComplete();
    } catch (err: any) {
      setError(err?.message || 'Failed to upload image');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoSelect = (photo: UserAssets['rows'][0]) => {
    setSelectedPhoto(photo);
  };

  const handleUseSelectedPhoto = async () => {
    if (!selectedPhoto?.id) {
      setError('Please select a photo first');
      return;
    }

    setUploading(true);
    setError('');

    getAssetsById({
      id: selectedPhoto.id
    })
      .unwrap()
      .then(async (res: any) => {
        if (res?.data) {
          const assetData = res.data;
          const faceDetections = assetData?.faceDetections || [];

          let currentUserId = null;
          if (isPeopleProfilePic) {
            const peopleAsset = assetData?.people?.find(
              (person: any) => person.id === peopleId
            );
            if (peopleAsset?.userId) {
              currentUserId = peopleAsset.userId;
            }
          } else {
            currentUserId = userId;
          }

          if (currentUserId) {
            const faceAttributes = faceDetections?.find(
              (face: any) => face.userId === currentUserId
            );
            if (faceAttributes) {
              const imageFile = await extractFaceFromImage(
                assetData.url,
                faceAttributes.boundingBox,
                `profile_${selectedPhoto.id}.jpg`
              );
              setUploading(true);
              await handleUpload(imageFile);
              return;
            }
          }
          // Create initial image URL and open cropper for all cases
          const imageUrl = URL.createObjectURL(
            new Blob([await fetch(assetData.url).then(r => r.blob())])
          );
          // Open cropper with square image
          setSelectedImageUrl(imageUrl);
          setShowCropper(true);
        }
      })
      .catch((err: any) => {
        console.error('Error fetching asset details:', err);
        setError('Failed to fetch selected photo details');
        setUploading(false);
        return;
      })
      .finally(() => {
        setUploading(false);
      });

    /* 

    try {
      if (isPeopleProfilePic) {
        const peopleDataObj = {
          id: peopleId || '',
          updatePeople: {
            profileImage: selectedPhoto?.url
          }
        };

        const peopleRes: any = await UpdatePeople(peopleDataObj);

        if (!peopleRes?.data) {
          throw new Error(
            peopleRes?.error || 'Failed to update people profile image'
          );
        }

        dispatch(
          setAlertDetails({
            alert: {
              type: 'success',
              message: t('proPicSuccess')
            },
            scope: 'people'
          })
        );
        await handleUploadComplete();
        onClose();
      } else {
        const userDataObj = {
          createUserDetails: {
            profileImage: selectedPhoto?.url
          }
        };

        const userRes: any = await UpdateUserDetails(userDataObj);

        if (!userRes?.data) {
          throw new Error(
            userRes?.error || 'Failed to update user profile image'
          );
        }

        dispatch(
          setAlertDetails({
            alert: {
              type: 'success',
              message: t('applySuccess')
            },
            scope: 'profile'
          })
        );

        navigate(routePaths.MyProfile);
      }

      await handleUploadComplete();
    } catch (err: any) {
      setError(err?.message || 'Failed to update profile image');
      console.error('Update error:', err);
    } finally {
      setUploading(false);
    } */
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if cropper is open
      if (showCropper) return;

      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
        setError('');
        setErrorFiles([]);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup previews
      files.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, [isOpen, onClose, files, showCropper]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70]">
        <div
          ref={modalRef}
          className={`bg-white rounded-2xl shadow-lg relative flex flex-col ${
            isViewMore ? 'w-3/4 h-[90vh]' : 'w-[480px] max-h-[90vh]'
          }`}>
          {/* Close Button */}
          <Button
            btnStyle="link"
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50 z-10"
            onClick={onClose}
            disabled={uploading}>
            <SVGAssets.CloseIconGray className="w-3 h-3" />
          </Button>

          {/* Scrollable Content Area */}
          <div
            className={`flex-1 overflow-y-auto p-6 ${isViewMore ? 'pb-0' : ''}`}>
            {/* Title */}
            <h2 className="text-lg font-semibold text-[#353535] mb-2">
              {t('Choose your profile picture')}
            </h2>

            {/* Error Message */}
            {error && (
              <UploadErrorBlock error={error} errorFiles={errorFiles} />
            )}

            {/* Upload Section */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragActive
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300'
              } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
              <input {...getInputProps()} disabled={uploading} />
              <div className="flex flex-col items-center justify-center text-[#353535]">
                {files.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="flex items-center justify-center mb-3">
                      <SVGAssets.ModalUploadIcon className="w-14 h-14" />
                    </div>
                    <p className="font-medium">{t('Upload your files here')}</p>
                    <p className="text-sm text-gray-500">
                      {t('dashboard.dragImageHere')}
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 flex justify-center">
                    <div className="flex flex-wrap justify-center gap-3">
                      {files.map(file => (
                        <div key={file.name} className="relative">
                          <img
                            src={file.preview}
                            alt="preview"
                            className="rounded-lg h-24 w-24 object-cover"
                          />
                          <Button
                            btnStyle="link"
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setFiles([]);
                              setError('');
                              setErrorFiles([]);
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">
                            <SVGAssets.CloseIconGray className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <SVGAssets.AddRoundIcon className="w-4 h-4" />
                  <p className="text-[#7F9D48] font-medium underline mt-1">
                    {t('Browse file to upload')}
                  </p>
                </div>
              </div>
            </div>

            {/* Gallery Section */}
            {!hideGallerySection && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[#353535]">
                    {t('photosPage.uploadPhotos')}
                  </h3>
                  {assetData?.data?.count > 0 && (
                    <Button
                      btnStyle="link"
                      className="text-sm text-[#7F9D48] font-medium"
                      onClick={() => setIsViewMore(!isViewMore)}>
                      {isViewMore ? t('viewLess') : t('viewMore')}
                    </Button>
                  )}
                </div>

                {assetData?.data?.rows && assetData.data.rows.length > 0 ? (
                  <div
                    className={`grid gap-2 grid-cols-[repeat(auto-fill,minmax(90px,1fr))]`}>
                    {assetData.data.rows.map((photo: any, index: number) => (
                      <div
                        key={photo.id || index}
                        onClick={() => handlePhotoSelect(photo)}
                        className={`relative cursor-pointer rounded-lg overflow-hidden w-20 h-20 border-2 transition-all ${
                          selectedPhoto?.id === photo.id
                            ? 'border-[#7F9D48] shadow-md'
                            : 'border-transparent hover:border-gray-300'
                        }`}>
                        <img
                          src={
                            photo?.thumbNailUrl ||
                            photo?.asset?.thumbNailUrl ||
                            photo?.url ||
                            '/placeholder-image.jpg'
                          }
                          alt={photo.name || `Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />

                        {selectedPhoto?.id === photo.id && (
                          <div className="absolute inset-0 bg-[#7F9D48]/40 flex items-center justify-center">
                            {/* <div className="w-6 h-6 bg-[#7F9D48] rounded-full flex items-center justify-center">
                              <SVGAssets.TickIcon className="w-4 h-4 text-[#7F9D48]" />
                            </div> */}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    {t('photosPage.noPhotosYet')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div
            className={`flex justify-between p-3 pt-4 border-t border-gray-200 bg-white ${isViewMore ? 'rounded-b-2xl' : ''}`}>
            <Button
              onClick={onClose}
              disabled={uploading}
              className="cancel-btn !text-sm">
              {t('cancel')}
            </Button>
            <Button
              btnStyle="primary"
              onClick={() =>
                selectedPhoto?.id ? handleUseSelectedPhoto() : handleUpload()
              }
              disabled={uploading || (files.length === 0 && !selectedPhoto?.id)}
              className="bg-[#7F9D48] text-white font-medium px-5 py-2 rounded-md hover:bg-[#6d8c3e] transition disabled:opacity-50 disabled:cursor-not-allowed !text-sm">
              {uploading
                ? t('dashboard.uploading')
                : t('Save as profile picture')}
            </Button>
          </div>
        </div>
      </div>

      {/* Image Cropper Modal */}
      {showCropper && (
        <ImageCropperModal
          isOpen={showCropper}
          imageUrl={selectedImageUrl}
          onClose={handleCropperClose}
          onCropComplete={handleCropComplete}
        />
      )}
    </>
  );
};
